import type { FeatureFlagOp } from './featureFlagOp';

type MutableFlags = Record<string, boolean>;
type MutableConfig = {
  allAdvisors: MutableFlags;
  advisors: Record<string, MutableFlags>;
  allClients: MutableFlags;
  clients: Record<string, MutableFlags>;
};

function cloneConfig(config: AppConfigSchema): MutableConfig {
  return {
    allAdvisors: { ...(config.allAdvisors ?? {}) } as MutableFlags,
    advisors: Object.fromEntries(
      Object.entries(config.advisors ?? {}).map(([id, flags]) => [
        id,
        { ...(flags ?? {}) } as MutableFlags,
      ]),
    ),
    allClients: { ...(config.allClients ?? {}) } as MutableFlags,
    clients: Object.fromEntries(
      Object.entries(config.clients ?? {}).map(([id, flags]) => [
        id,
        { ...(flags ?? {}) } as MutableFlags,
      ]),
    ),
  };
}

/**
 * Deterministically apply an ordered list of ops to a config snapshot.
 *
 * Pure: input is never mutated.
 * Empty `advisors[id] = {}` sections are pruned so the JSON stays minimal.
 */
export function applyFeatureFlagOperations(
  config: AppConfigSchema,
  operations: readonly FeatureFlagOp[],
): AppConfigSchema {
  const next = cloneConfig(config);

  for (const op of operations) {
    switch (op.type) {
      case 'SET_ALL_ADVISORS':
        next.allAdvisors[op.flagKey] = op.value;
        break;
      case 'REMOVE_ALL_ADVISORS':
        delete next.allAdvisors[op.flagKey];
        break;
      case 'SET_ADVISOR_FLAG': {
        const section = next.advisors[op.advisorId] ?? {};
        section[op.flagKey] = op.value;
        next.advisors[op.advisorId] = section;
        break;
      }
      case 'REMOVE_ADVISOR_FLAG': {
        const section = next.advisors[op.advisorId];
        if (section) {
          delete section[op.flagKey];
        }
        break;
      }
      case 'REMOVE_ADVISOR_SECTION':
        delete next.advisors[op.advisorId];
        break;
      case 'SET_CLIENT_FLAG': {
        const section = next.clients[op.clientId] ?? {};
        section[op.flagKey] = op.value;
        next.clients[op.clientId] = section;
        break;
      }
    }
  }

  for (const [advisorId, section] of Object.entries(next.advisors)) {
    if (Object.keys(section).length === 0) {
      delete next.advisors[advisorId];
    }
  }

  return next as AppConfigSchema;
}
